import * as THREE from 'three';
import { NEST_REVEAL_LERP } from '../../constants';
import { diskR, nestDepthOf, setCameraBasis, viewAlong } from './camera';
import {
  minXYClear,
  minXYClearHang,
  minXYClearHangDisk,
  minXYClearTitle,
  sameDepthYields,
} from './clearance';
import { releaseShove, shoveHoldsSide, smoothGoal } from './grade';
import { collectLabelOccluders, collectTitleOccluders, ensureHome, hangBoxAt, livePos } from './occluders';
import { CLEAR_PASSES, capTotal, setClearTight, setScreenPad } from './pads';
import { REVEAL_BROADPHASE, createBoxGrid, createRevealGrid } from './broadphase';

function revealEase(delta, reducedMotion, tight) {
  const dt = Math.min(delta || 0.016, 0.032);
  const lerp = tight ? NEST_REVEAL_LERP : NEST_REVEAL_LERP * 0.48;
  const k = reducedMotion ? 1 : 1 - Math.pow(1 - lerp, dt * 60);
  return k;
}

/** Keep easing toward the last solved shove. Skips the overlap search. */
function easeStoredReveal(meshByTaskId, k) {
  const moved = new Set();
  meshByTaskId.forEach((mesh, tid) => {
    const ud = mesh.userData;
    const home = ensureHome(ud, mesh);
    const hidden = ud.exiting || (ud.visT ?? 1) < 0.02;
    const goalX = hidden ? 0 : (ud.shoveGX ?? ud.revealX ?? 0);
    const goalY = hidden ? 0 : (ud.shoveGY ?? ud.revealY ?? 0);
    if (hidden) ud.titleClearing = false;
    const applyK = ud.layoutJump ? 1 : k;
    ud.revealX = (ud.revealX ?? 0) + (goalX - (ud.revealX ?? 0)) * applyK;
    ud.revealY = (ud.revealY ?? 0) + (goalY - (ud.revealY ?? 0)) * applyK;
    const x = home.x + ud.revealX;
    const y = home.y + ud.revealY;
    const z = home.z;
    const dx = mesh.position.x - x;
    const dy = mesh.position.y - y;
    const dz = mesh.position.z - z;
    if (dx * dx + dy * dy + dz * dz > 1e-7) moved.add(tid);
    mesh.position.set(x, y, z);
  });
  return { moved };
}

/**
 * Shallower missions keep their ground. Every child yields to every parent
 * (any family), then to same-depth hubs in front (sticky while the camera
 * orbits). Nodes flee only shallower names. Nested names slide off parent
 * names and parent disks. Cluster titles still shove everyone. Solve from
 * the last seat so the nearest-exit side cannot flip each frame.
 */
export function writeRevealedPositions(meshByTaskId, camera, delta, reducedMotion, nebula, container, freeze = false, labelByTaskId, manualClear = true, holdGoals = false) {
  if (freeze) {
    const moved = new Set();
    meshByTaskId.forEach((mesh, tid) => {
      const home = ensureHome(mesh.userData, mesh);
      const x = home.x + (mesh.userData.revealX ?? 0);
      const y = home.y + (mesh.userData.revealY ?? 0);
      const z = home.z;
      const dx = mesh.position.x - x;
      const dy = mesh.position.y - y;
      const dz = mesh.position.z - z;
      if (dx * dx + dy * dy + dz * dz > 1e-7) moved.add(tid);
      mesh.position.set(x, y, z);
    });
    return { moved };
  }
  const dt = Math.min(delta || 0.016, 0.032);
  const tight = manualClear !== false;
  const k = revealEase(delta, reducedMotion, tight);
  if (holdGoals) return easeStoredReveal(meshByTaskId, k);
  setClearTight(tight);
  const passes = tight ? CLEAR_PASSES : 2;
  const goalFollow = tight ? 1 : 1 - Math.pow(1 - 0.1, dt * 60);
  const moved = new Set();
  const targets = new Map();
  if (!setCameraBasis(camera)) {
    meshByTaskId.forEach((mesh) => {
      const home = ensureHome(mesh.userData, mesh);
      mesh.position.copy(home);
    });
    return { moved };
  }
  const titles = collectTitleOccluders(nebula, camera, container);
  const names = collectLabelOccluders(labelByTaskId, camera, container, meshByTaskId);
  const cw = Math.max(container?.clientWidth || 1, 1);
  const ch = Math.max(container?.clientHeight || 1, 1);
  const halfHAt1 = Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5);

  const entries = [];
  meshByTaskId.forEach((mesh, tid) => {
    const ud = mesh.userData;
    if (ud.exiting || (ud.visT ?? 1) < 0.02) return;
    const home = ensureHome(ud, mesh);
    entries.push({
      mesh,
      tid,
      depth: nestDepthOf(mesh),
      viewZ: viewAlong(home, camera),
    });
  });
  entries.sort((a, b) => a.depth - b.depth || a.viewZ - b.viewZ);
  let refZ = 12;
  if (entries.length) {
    let sum = 0;
    for (let i = 0; i < entries.length; i += 1) sum += entries[i].viewZ;
    refZ = sum / entries.length;
  }
  setScreenPad(camera, container, refZ);

  const trial = { x: 0, y: 0, z: 0 };
  const anyJump = entries.some((e) => e.mesh.userData.layoutJump);
  // Only neighbours that can actually touch get tested. Same order as the
  // full scan, so the accumulated shove is identical.
  const grid = REVEAL_BROADPHASE.enabled && !anyJump ? createRevealGrid(entries, camera, targets) : null;
  const nameGrid = grid ? createBoxGrid(names) : null;
  const near = [];

  entries.forEach((entry, entryIdx) => {
    const ud = entry.mesh.userData;
    if (ud.layoutJump || anyJump) {
      if (ud.layoutJump) {
        ud.shoveGX = 0;
        ud.shoveGY = 0;
        ud.releasing = false;
      }
      targets.set(entry.tid, ud.layoutJump
        ? { x: 0, y: 0, titleClearing: false }
        : { x: ud.revealX ?? 0, y: ud.revealY ?? 0, titleClearing: ud.titleClearing === true });
      return;
    }
    const home = ensureHome(entry.mesh.userData, entry.mesh);
    let gx = entry.mesh.userData.revealX ?? 0;
    let gy = entry.mesh.userData.revealY ?? 0;
    trial.x = home.x + gx;
    trial.y = home.y + gy;
    trial.z = home.z;
    const selfR = diskR(entry.mesh);
    const titleHeld = entry.mesh.userData.titleClearing === true;
    const stickyFront = entry.mesh.userData.yieldToIds;
    const nextFront = new Set();
    let clearing = false;

    const applyPush = (push) => {
      gx += push.x;
      gy += push.y;
      const capped = capTotal(gx, gy);
      gx = capped.x;
      gy = capped.y;
      trial.x = home.x + gx;
      trial.y = home.y + gy;
      clearing = true;
    };

    /** Disk-vs-disk yield against one shallower / in-front neighbour. */
    const clearDisk = (other) => {
      if (other.tid === entry.tid) return false;
      if (other.depth > entry.depth) return false;
      const yieldToParent = other.depth < entry.depth;
      if (!yieldToParent && !sameDepthYields(entry.viewZ, other.viewZ, stickyFront?.has(other.tid))) {
        return false;
      }
      const occ = livePos(other.mesh, other.tid, targets);
      const push = minXYClear(camera, occ, trial, diskR(other.mesh), selfR, yieldToParent, titleHeld);
      if (push.x === 0 && push.y === 0) return false;
      if (!yieldToParent) nextFront.add(other.tid);
      applyPush(push);
      return true;
    };

    /** Hanging name vs one shallower disk. `hang` is fixed for the sweep. */
    const clearHangDisk = (other, hang) => {
      if (other.tid === entry.tid || other.depth >= entry.depth) return false;
      const occ = livePos(other.mesh, other.tid, targets);
      const push = minXYClearHangDisk(camera, occ, diskR(other.mesh), hang);
      if (push.x === 0 && push.y === 0) return false;
      applyPush(push);
      return true;
    };

    /** Own disk vs one shallower name box. */
    const clearName = (name) => {
      if (name.tid === entry.tid || name.depth >= entry.depth) return false;
      const push = minXYClearTitle(camera, name, trial, selfR);
      if (push.x === 0 && push.y === 0) return false;
      applyPush(push);
      return true;
    };

    /** Hanging name vs one shallower name box. `hang` is fixed for the sweep. */
    const clearHangName = (name, hang) => {
      if (name.tid === entry.tid || name.depth >= entry.depth) return false;
      const push = minXYClearHang(camera, name, hang);
      if (push.x === 0 && push.y === 0) return false;
      applyPush(push);
      return true;
    };

    for (let pass = 0; pass < passes; pass += 1) {
      let pushed = false;
      if (entry.depth > 0) {
        if (grid) {
          // Neighbours are fetched for the current seat with some slack. When
          // a push drifts the seat past that slack, refetch and carry on past
          // the last index already visited — anything skipped could not have
          // overlapped the seat it was tested at.
          let list = grid.queryDisk(trial, selfR, near);
          let lastIdx = -1;
          let c = 0;
          while (c < list.length) {
            const oi = list[c];
            c += 1;
            if (oi <= lastIdx) continue;
            lastIdx = oi;
            if (clearDisk(entries[oi])) {
              pushed = true;
              if (grid.stale(trial, selfR)) {
                list = grid.queryDisk(trial, selfR, near);
                c = 0;
              }
            }
          }
        } else {
          for (let i = 0; i < entries.length; i += 1) {
            if (clearDisk(entries[i])) pushed = true;
          }
        }
      }
      for (let t = 0; t < titles.length; t += 1) {
        const push = minXYClearTitle(camera, titles[t], trial, selfR);
        if (push.x === 0 && push.y === 0) continue;
        clearing = true;
        gx += push.x;
        gy += push.y;
        const capped = capTotal(gx, gy);
        gx = capped.x;
        gy = capped.y;
        trial.x = home.x + gx;
        trial.y = home.y + gy;
        pushed = true;
      }
      if (entry.depth > 0) {
        if (nameGrid) {
          let list = nameGrid.queryDisk(trial, selfR, camera, near);
          let lastIdx = -1;
          let c = 0;
          while (c < list.length) {
            const ni = list[c];
            c += 1;
            if (ni <= lastIdx) continue;
            lastIdx = ni;
            if (clearName(names[ni])) {
              pushed = true;
              if (nameGrid.stale(trial, selfR, camera)) {
                list = nameGrid.queryDisk(trial, selfR, camera, near);
                c = 0;
              }
            }
          }
        } else {
          for (let n = 0; n < names.length; n += 1) {
            if (clearName(names[n])) pushed = true;
          }
        }
        const rec = labelByTaskId?.get(entry.tid);
        let hang = hangBoxAt(camera, trial, entry.mesh, rec, cw, ch, halfHAt1);
        if (hang) {
          if (nameGrid) {
            const list = nameGrid.queryBox(hang, near);
            for (let c = 0; c < list.length; c += 1) {
              if (clearHangName(names[list[c]], hang)) pushed = true;
            }
          } else {
            for (let n = 0; n < names.length; n += 1) {
              if (clearHangName(names[n], hang)) pushed = true;
            }
          }
        }
        hang = hangBoxAt(camera, trial, entry.mesh, rec, cw, ch, halfHAt1);
        if (hang) {
          if (grid) {
            const list = grid.queryBox(hang, near);
            for (let c = 0; c < list.length; c += 1) {
              if (clearHangDisk(entries[list[c]], hang)) pushed = true;
            }
          } else {
            for (let i = 0; i < entries.length; i += 1) {
              if (clearHangDisk(entries[i], hang)) pushed = true;
            }
          }
        }
      }
      if (!pushed) break;
    }
    if (!clearing) {
      gx = 0;
      gy = 0;
    } else if (titleHeld) {
      const held = shoveHoldsSide(
        entry.mesh.userData.revealX ?? 0,
        entry.mesh.userData.revealY ?? 0,
        gx,
        gy,
      );
      gx = held.x;
      gy = held.y;
    }
    const liveX = ud.revealX ?? 0;
    const liveY = ud.revealY ?? 0;
    const prevGX = ud.shoveGX ?? liveX;
    const prevGY = ud.shoveGY ?? liveY;
    const released = releaseShove(liveX, liveY, gx, gy, clearing, ud.releasing === true);
    ud.releasing = released.releasing;
    const follow = released.releasing ? Math.min(goalFollow, 1 - Math.pow(1 - 0.07, dt * 60)) : goalFollow;
    const smoothed = smoothGoal(prevGX, prevGY, released.x, released.y, follow);
    const out = Math.hypot(smoothed.x, smoothed.y) > 0.008 || Math.hypot(liveX, liveY) > 0.02;
    if (!out) {
      ud.shoveGX = 0;
      ud.shoveGY = 0;
      ud.releasing = false;
      targets.set(entry.tid, { x: 0, y: 0, titleClearing: false });
    } else {
      ud.shoveGX = smoothed.x;
      ud.shoveGY = smoothed.y;
      targets.set(entry.tid, {
        x: smoothed.x,
        y: smoothed.y,
        titleClearing: true,
      });
    }
    entry.mesh.userData.yieldToIds = nextFront;
    grid?.moved(entryIdx);
  });

  meshByTaskId.forEach((mesh, tid) => {
    const ud = mesh.userData;
    const home = ensureHome(ud, mesh);
    const goal = targets.get(tid) || { x: 0, y: 0 };
    ud.titleClearing = goal.titleClearing === true;
    const applyK = ud.layoutJump ? 1 : k;
    ud.revealX = (ud.revealX ?? 0) + (goal.x - (ud.revealX ?? 0)) * applyK;
    ud.revealY = (ud.revealY ?? 0) + (goal.y - (ud.revealY ?? 0)) * applyK;
    const x = home.x + ud.revealX;
    const y = home.y + ud.revealY;
    const z = home.z;
    const dx = mesh.position.x - x;
    const dy = mesh.position.y - y;
    const dz = mesh.position.z - z;
    if (dx * dx + dy * dy + dz * dz > 1e-7) {
      moved.add(tid);
    }
    mesh.position.set(x, y, z);
  });

  return { moved };
}
